import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import RichTextEditor from '../../../components/admin/cms/RichTextEditor';
import RevisionHistory from '../../../components/admin/cms/RevisionHistory';
import DiffView from '../../../components/admin/cms/DiffView';
import { formatApiError } from '../../../lib/apiError';
import { useToast } from '../../../hooks/use-toast';
import axios from 'axios';
import {
  ArrowLeft, Save, Send, Loader2, AlertTriangle, RefreshCw, CheckCircle2,
  Cloud, CloudOff, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTOSAVE_MS = 1500;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30' },
  published: { label: 'Published', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  archived: { label: 'Archived', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
};

const EditorSkeleton = () => (
  <div className="animate-pulse space-y-4" data-testid="cms-editor-skeleton">
    <div className="h-8 w-64 bg-slate-700/50 rounded" />
    <div className="grid grid-cols-3 gap-6">
      <div className="col-span-2 space-y-3">
        <div className="h-10 bg-slate-700/40 rounded" />
        <div className="h-72 bg-slate-700/30 rounded-lg" />
      </div>
      <div className="space-y-3">
        <div className="h-40 bg-slate-700/30 rounded-lg" />
        <div className="h-40 bg-slate-700/30 rounded-lg" />
      </div>
    </div>
  </div>
);

const PageEditor = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [userMap, setUserMap] = useState({});

  const [meta, setMeta] = useState({ title: '', slug: '', visibility: 'private', locale: 'en', order: 0 });
  const [bodyHtml, setBodyHtml] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle|saving|saved|error
  const [editorKey, setEditorKey] = useState(0);

  const [showPublish, setShowPublish] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [compare, setCompare] = useState(null); // {a, b}

  const lastUpdatedAt = useRef(null);
  const savingRef = useRef(false);
  const contentRef = useRef({}); // preserve non-body content keys

  const applyPage = useCallback((p) => {
    setPage(p);
    setMeta({ title: p.title, slug: p.slug, visibility: p.visibility, locale: p.locale, order: p.order });
    contentRef.current = p.content || {};
    setBodyHtml((p.content && p.content.body_html) || '');
    lastUpdatedAt.current = p.updated_at;
    setEditorKey((k) => k + 1);
    setDirty(false);
    setSaveState('idle');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${API_URL}/api/admin/cms/pages/${pageId}`);
      applyPage(res.data);
    } catch (e) {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [pageId, applyPage]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/users`)
      .then((r) => {
        const map = {};
        (r.data || []).forEach((u) => { map[u.id] = u; });
        setUserMap(map);
      })
      .catch(() => {});
  }, []);

  // ---- Save (draft) with optimistic locking -------------------------------
  const save = useCallback(async (manual) => {
    if (savingRef.current || conflict) return false;
    savingRef.current = true;
    setSaveState('saving');
    try {
      const payload = {
        title: meta.title,
        slug: meta.slug,
        visibility: meta.visibility,
        locale: meta.locale,
        order: Number(meta.order) || 0,
        content: { ...contentRef.current, body_html: bodyHtml },
        expected_updated_at: lastUpdatedAt.current,
      };
      const res = await axios.put(`${API_URL}/api/admin/cms/pages/${pageId}`, payload);
      lastUpdatedAt.current = res.data.updated_at;
      contentRef.current = res.data.content || contentRef.current;
      setPage(res.data);
      setDirty(false);
      setSaveState('saved');
      if (manual) toast({ title: 'Draft saved' });
      return true;
    } catch (e) {
      setSaveState('error');
      if (e.response?.status === 409) {
        setConflict(true);
      } else {
        toast({ title: 'Save failed', description: formatApiError(e, 'Could not save draft'), variant: 'destructive' });
      }
      return false;
    } finally {
      savingRef.current = false;
    }
  }, [meta, bodyHtml, pageId, conflict, toast]);

  // ---- Autosave (debounced, only when dirty) ------------------------------
  useEffect(() => {
    if (loading || !dirty || conflict) return undefined;
    const t = setTimeout(() => { save(false); }, AUTOSAVE_MS);
    return () => clearTimeout(t);
  }, [bodyHtml, meta, dirty, conflict, loading, save]);

  // ---- Unsaved-changes protection -----------------------------------------
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const markDirty = () => { setDirty(true); if (saveState === 'saved') setSaveState('idle'); };
  const onMeta = (patch) => { setMeta((m) => ({ ...m, ...patch })); markDirty(); };
  const onBody = (html) => { setBodyHtml(html); markDirty(); };

  const goBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    navigate('/admin/cms');
  };

  // ---- Publish workflow ---------------------------------------------------
  const doPublish = async () => {
    setPublishing(true);
    try {
      if (dirty) {
        const ok = await save(false);
        if (!ok) { setPublishing(false); return; }
      }
      const res = await axios.post(`${API_URL}/api/admin/cms/pages/${pageId}/publish`,
        { revision_notes: revisionNotes || null });
      applyPage(res.data);
      setShowPublish(false);
      setRevisionNotes('');
      toast({ title: 'Page published', description: `Now at version v${res.data.version}` });
    } catch (e) {
      if (e.response?.status === 409) {
        setShowPublish(false);
        setConflict(true);
      } else {
        toast({ title: 'Publish failed', description: formatApiError(e, 'Could not publish'), variant: 'destructive' });
      }
    } finally {
      setPublishing(false);
    }
  };

  const reloadFromServer = async () => {
    setConflict(false);
    await load();
    toast({ title: 'Reloaded latest version' });
  };

  const saveIndicator = () => {
    if (saveState === 'saving') return { icon: <Loader2 className="w-4 h-4 animate-spin" />, text: 'Saving…', cls: 'text-amber-400' };
    if (saveState === 'saved') return { icon: <Cloud className="w-4 h-4" />, text: 'All changes saved', cls: 'text-emerald-400' };
    if (saveState === 'error') return { icon: <CloudOff className="w-4 h-4" />, text: 'Save error', cls: 'text-red-400' };
    if (dirty) return { icon: <CloudOff className="w-4 h-4" />, text: 'Unsaved changes', cls: 'text-slate-400' };
    return { icon: <Cloud className="w-4 h-4" />, text: 'Up to date', cls: 'text-slate-500' };
  };
  const ind = saveIndicator();
  const statusMeta = STATUS_CONFIG[page?.status] || STATUS_CONFIG.draft;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={goBack} data-testid="cms-editor-back" className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white" data-testid="cms-editor-title">Edit Page</h1>
            <div className={`flex items-center gap-1.5 text-sm mt-0.5 ${ind.cls}`} data-testid="cms-save-indicator">
              {ind.icon}<span>{ind.text}</span>
            </div>
          </div>
        </div>
        {!loading && !loadError && (
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 text-xs font-medium rounded border ${statusMeta.color}`} data-testid="cms-editor-status">{statusMeta.label}</span>
            <button onClick={() => save(true)} disabled={saveState === 'saving'} data-testid="cms-editor-save-btn"
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white font-medium rounded-lg hover:bg-slate-600 disabled:opacity-50 transition-colors">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={() => setShowPublish(true)} data-testid="cms-editor-publish-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors">
              <Send className="w-4 h-4" /> Publish
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <EditorSkeleton />
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="cms-editor-error">
          <AlertTriangle className="w-12 h-12 text-red-400 mb-3" />
          <p className="text-white font-medium mb-1">Couldn’t load this page</p>
          <p className="text-slate-400 text-sm mb-4">Please check your connection and try again.</p>
          <button onClick={load} data-testid="cms-editor-retry"
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors">
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Editor column */}
          <div className="lg:col-span-2 space-y-4">
            <input type="text" value={meta.title} onChange={(e) => onMeta({ title: e.target.value })}
              placeholder="Page title" data-testid="cms-editor-title-input"
              className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white text-lg font-semibold focus:outline-none focus:border-amber-500" />
            <RichTextEditor key={editorKey} value={bodyHtml} onChange={onBody} />
          </div>

          {/* Side panel */}
          <div className="space-y-6">
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5 space-y-4">
              <h3 className="text-white font-semibold text-sm">Page Settings</h3>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Slug</label>
                <input type="text" value={meta.slug} onChange={(e) => onMeta({ slug: e.target.value })}
                  data-testid="cms-editor-slug-input"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Visibility</label>
                  <select value={meta.visibility} onChange={(e) => onMeta({ visibility: e.target.value })}
                    data-testid="cms-editor-visibility-select"
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500">
                    <option value="private">Private</option>
                    <option value="public">Public</option>
                    <option value="hidden">Hidden</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Locale</label>
                  <input type="text" value={meta.locale} onChange={(e) => onMeta({ locale: e.target.value })}
                    data-testid="cms-editor-locale-input"
                    className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Order</label>
                <input type="number" value={meta.order} onChange={(e) => onMeta({ order: e.target.value })}
                  data-testid="cms-editor-order-input"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <p className="text-xs text-slate-500">Version v{page?.version} · updated {page ? new Date(page.updated_at).toLocaleString() : ''}</p>
            </div>

            <RevisionHistory revisions={page?.revisions || []} userMap={userMap} onCompare={(a, b) => setCompare({ a, b })} />
          </div>
        </div>
      )}

      {/* Publish dialog */}
      {showPublish && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPublish(false)} />
          <div className="relative bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md mx-4 shadow-2xl" data-testid="cms-publish-dialog">
            <div className="flex items-center justify-between p-5 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Send className="w-5 h-5 text-amber-400" /> Publish Page</h2>
              <button onClick={() => setShowPublish(false)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-300">This will publish the current draft and create a new revision.</p>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Revision notes (optional)</label>
                <textarea rows={3} value={revisionNotes} onChange={(e) => setRevisionNotes(e.target.value)}
                  placeholder="e.g. Updated hero title" data-testid="cms-revision-notes-input"
                  className="w-full px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-amber-500" />
              </div>
              <div className="flex justify-end gap-3 pt-1">
                <button onClick={() => setShowPublish(false)} className="px-4 py-2 text-slate-400 hover:text-white transition-colors">Cancel</button>
                <button onClick={doPublish} disabled={publishing} data-testid="cms-confirm-publish-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors">
                  {publishing ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</> : <><CheckCircle2 className="w-4 h-4" /> Confirm & Publish</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conflict dialog */}
      {conflict && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative bg-slate-800 border border-red-500/40 rounded-xl w-full max-w-md mx-4 shadow-2xl" data-testid="cms-conflict-dialog">
            <div className="p-5 border-b border-slate-700 flex items-center gap-2 text-white">
              <AlertTriangle className="w-5 h-5 text-red-400" /> <span className="text-lg font-semibold">Editing conflict</span>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-300">
                This page was modified by another admin since you loaded it. To avoid overwriting their changes,
                reload the latest version. Your unsaved edits here will be discarded.
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setConflict(false)} data-testid="cms-conflict-dismiss"
                  className="px-4 py-2 text-slate-400 hover:text-white transition-colors">Keep editing</button>
                <button onClick={reloadFromServer} data-testid="cms-conflict-reload"
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition-colors">
                  <RefreshCw className="w-4 h-4" /> Reload latest
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Version compare */}
      {compare && <DiffView a={compare.a} b={compare.b} onClose={() => setCompare(null)} />}
    </AdminLayout>
  );
};

export default PageEditor;
